/* eslint-disable no-unneeded-ternary */
import React, { useState } from 'react'
import { withTranslator } from '../HOCComponents/withTranslator'
import styled from 'styled-components'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import moment from 'moment'

// eslint-disable-next-line import/no-duplicates
import { useDroppable } from '@dnd-kit/core'
// eslint-disable-next-line import/no-duplicates
import { useDndMonitor } from '@dnd-kit/core'

import { trackEvent } from '../analytics/analytics'
import { TRACK_EVENTS } from '../../constants'

const TemporalComparisonContainer = styled.section`
  margin-bottom: 16px;
`

const TemporalComparisonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: -15px 0 0;
  padding: 20px 0 10px;
`

const Text = styled.p`
  color: ${themeColors.whiteTransparent08};
  margin: 0 15px;
`

const Title = styled.h1`
  color: ${themeColors.mintColor};
  margin-bottom: 15px;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: normal;
`

const DateField = styled.div`
  width: 110px;
  height: 30px;
  border-radius: 999px;
  color: ${themeColors.deepblueColor3};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${themeColors.mintColor};
`

// The empty slot is a dashed outline that turns mint while a day is dragged. A dropped date fills it like the current
// date, and a day that can't be compared turns it red.
const dropZoneLook = ({ isDisabled, droppableDate, isOver, isDragging }) => {
  if (isDisabled) {
    return { border: `2px solid ${themeColors.dangerRed}`, background: themeColors.dangerRedTransparent, color: themeColors.whiteColor }
  }
  if (droppableDate) {
    return { border: `2px solid ${themeColors.mintColor}`, background: themeColors.mintColor, color: themeColors.deepblueColor3 }
  }
  if (isOver) {
    return { border: `2px solid ${themeColors.mintColor}`, background: themeColors.mintColorTransparent, color: themeColors.whiteColor }
  }
  if (isDragging) {
    return { border: `2px dashed ${themeColors.mintColor}`, background: themeColors.mintColorHoverTransparent, color: themeColors.whiteColor }
  }
  return { border: `2px dashed ${themeColors.lightGrey}`, background: themeColors.transparent, color: themeColors.whiteTransparent08 }
}

const DropZone = styled.div`
  width: 110px;
  height: 30px;
  border-radius: 999px;
  color: ${props => dropZoneLook(props).color};
  /* Block (not flex) so the ellipsis works; line-height centers within the 26px inside the 2px border. */
  text-align: center;
  line-height: 26px;
  padding: 0 6px;
  transform: ${props => (props.isOver && !props.isDisabled ? 'scale(1.14)' : props.isDragging && !props.isDisabled ? 'scale(1.08)' : 'scale(1)')};
  transition: 0.25s ease all;
  border: ${props => dropZoneLook(props).border};
  background: ${props => dropZoneLook(props).background};
  box-shadow: ${props => (props.isOver && !props.isDisabled ? `0 0 0 3px ${themeColors.mintColorTransparent}` : 'none')};
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  overflow: hidden;
`

const formatDate = date => moment(date).format('D MMM. YYYY')

function TemporalComparison({ t, selectedFlightGroupDate, tempComparisonFlightGroupDate, isComparable = true }) {
  const formattedDate = formatDate(selectedFlightGroupDate)

  const droppableDate = tempComparisonFlightGroupDate ? formatDate(tempComparisonFlightGroupDate) : null
  const { setNodeRef } = useDroppable({ id: 'drop-zone' })

  const [isDragging, setIsDragging] = useState(false)
  const [isOver, setIsOver] = useState(false)

  useDndMonitor({
    onDragStart() {
      setIsDragging(true)
    },
    onDragOver(event) {
      setIsOver(!!event.over)
    },
    onDragEnd(event) {
      setIsDragging(false)
      setIsOver(false)
      trackEvent(TRACK_EVENTS.FIELD_PANEL_TEMPORAL_COMPARE, {
        value: event.active.id,
        button: 'Temporal Comparison drop zone',
        location: TRACK_EVENTS.FIELD_PANEL
      })
    },
    onDragCancel() {
      setIsDragging(false)
      setIsOver(false)
    }
  })

  return (
    <TemporalComparisonContainer>
      <Title>{t('FLIGHT_FILTER')}</Title>
      <TemporalComparisonWrapper>
        <DateField activeDate={true}>{formattedDate}</DateField>
        <Text>vs</Text>
        <div style={{ display: 'flex', alignItems: 'center' }} ref={setNodeRef}>
          <DropZone isOver={isOver} droppableDate={droppableDate} isDragging={isDragging} isDisabled={!isComparable && isDragging}>
            {droppableDate ? droppableDate : !isComparable && isDragging ? <i className="fa-solid fa-ban" /> : t('TIME_COMPARISON_DROP_ZONE')}
          </DropZone>
        </div>
      </TemporalComparisonWrapper>
    </TemporalComparisonContainer>
  )
}

export default withTranslator(TemporalComparison)
